import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { get } from "lodash-es";

import { IStoreState } from "App/types";
import { API_ENDPOINTS } from "shared/constants";
import { IInfoBlockItemType, IDataKeys, IInfoBlockItem } from "shared/types";
import { InfoBlockItemService } from "shared/services";
import { BAND_ACTIONS } from "Bands/types";
import { getInfoBlockItemKey } from "Bands/utils";
import { isPageFetchedInState } from "../utils/helpers";

const INFO_BLOCK_ITEMS_PER_PAGE = 10;

interface InfoBlockItemsActionParams {
  bandId: string;
  type: IInfoBlockItem;
  page?: number;
  itemsPerPage?: number;
  paginationKey?: string;
}

export const dispatchGetInfoBlockItemsAction = (
  dispatch: Dispatch<any>,
  params: InfoBlockItemsActionParams
) => {
  const { bandId, type, page } = params;
  const paginationKey = getInfoBlockItemKey(bandId, type);

  return dispatch(getInfoBlockItems({ ...params, paginationKey, page: page || 1 }));
};

export const getInfoBlockItems = createAsyncThunk(
  BAND_ACTIONS.getInfoBlockItems,
  async ({ bandId, type, page, itemsPerPage }: InfoBlockItemsActionParams, { rejectWithValue }) => {
    try {
      const data = (await InfoBlockItemService.getInfoBlockItems(type, {
        band: API_ENDPOINTS.BAND(bandId),
        ...(page && { page }),
        itemsPerPage: itemsPerPage || INFO_BLOCK_ITEMS_PER_PAGE,
      })) as any;

      return {
        [type]: data[type],
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: ({ bandId, type, page }, { getState }) => {
      const state = getState() as IStoreState;

      // do not refetch if equipment page has already been fetched
      return !isPageFetchedInState(state, getInfoBlockItemKey(bandId, type), page || 1);
    },
  }
);

export const createInfoBlockItem = createAsyncThunk(
  BAND_ACTIONS.createInfoBlockItem,
  async (
    { item, type }: { item: Partial<IInfoBlockItemType>; type: IInfoBlockItem },
    { rejectWithValue }
  ) => {
    try {
      const data = await InfoBlockItemService.createInfoBlockItem(item, type);

      const createdItem = (Object.values(data?.[type]) as any)?.[0];

      return { createdItem, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getInfoBlockItem = createAsyncThunk(
  BAND_ACTIONS.getInfoBlockItem,
  async ({ id, type }: { id: string; type: IInfoBlockItem }, { rejectWithValue, getState }) => {
    try {
      // todo: add as condition, see infoBlock.getInfoBlock
      const itemExisits = get(getState() as IStoreState, [
        "data",
        "data",
        type,
        API_ENDPOINTS.INFO_BLOCK_ITEM(id, type),
      ]);

      if (itemExisits) {
        return;
      }

      return InfoBlockItemService.getInfoBlockItem(id, type);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editInfoBlockItem = createAsyncThunk(
  BAND_ACTIONS.editInfoBlockItem,
  async (
    { id, item, type }: { id: string; item: Partial<IInfoBlockItemType>; type: IInfoBlockItem },
    { rejectWithValue }
  ) => {
    try {
      return await InfoBlockItemService.editInfoBlockItem(id, item, type);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteInfoBlockItem = createAsyncThunk(
  BAND_ACTIONS.deleteInfoBlockItem,
  async (
    {
      id,
      type,
    }: {
      id: string;
      type: IInfoBlockItem;
      infoBlockId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      await InfoBlockItemService.deleteInfoBlockItem(id, type);
      return {
        path: API_ENDPOINTS.INFO_BLOCK_ITEM(id, type),
        dataKey: IDataKeys[type],
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
